<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useForm } from 'vee-validate';

import api from '@/api';
import { updateToken } from '@/api/backend/_http';
import { AppValues } from '@/helpers/const';
import { toast } from '@/helpers/toast';
import { isLoanAppComplete } from '@/helpers/typeGuards';
import { useApplicationApi } from '@/helpers/useApplicationApi';
import { useUserQuoteProcess } from '@/helpers/useUserQuoteProcess';
import { yupObject } from '@/validation';
import MarketingQuestions from '@/components/shared/MarketingQuestions.vue';

import type { ApplicationType } from '@/api/enums';
import type { Applicant } from '@/types';
import type { CustomerAwareOfBridgit } from '@/types/api/api-enums';

interface ReferralEntry {
  isRequired: boolean;
}

const { backupQuote, isLoading, submitScenario } = useApplicationApi();
const { loanApp, state, send, saveAndGoNext, goBack } = useUserQuoteProcess();
const { sectionStates, unaccreditedBroker } = state.value.context;

// Marketing questions state
const brokerOriginType = ref<string | undefined>(undefined);
const marketingReferralTypeId = ref<string | null>(null);
const marketingReferralOtherDescription = ref<string | null>(null);
const marketingCustomerAware = ref<CustomerAwareOfBridgit | null>(null);

// Fetch broker originType on mount
onMounted(async () => {
  if (loanApp.value.userType === 'Broker' && loanApp.value.brokerId) {
    try {
      const { data } = await api.backend.brokers.user.currentUser();
      brokerOriginType.value = data.broker.originType;
    } catch (error) {
      console.error('Failed to fetch broker originType:', error);
    }
  }
});

const { handleSubmit } = useForm<ReferralEntry>({
  validationSchema: yupObject({}),
  initialValues: { isRequired: loanApp.value.userType === 'Primary' },
  validateOnMount: false,
});

const saveAndContinue = handleSubmit(() => {
  const primaryApplicant = loanApp.value.applicants.find((a) => a.type === 'primary');
  let applicants: Applicant[];
  if (primaryApplicant) {
    applicants = [primaryApplicant, ...loanApp.value.applicants.filter((a) => a.type !== 'primary')];
  } else {
    applicants = loanApp.value.applicants;
  }
  send({
    type: 'UPDATE',
    loanApp: {
      ...loanApp.value,
      applicants,
      utmMetrics: {
        ...loanApp.value.utmMetrics,
      },
    },
  });
  submitApplicationQuote();
});

const submitApplicationQuote = async () => {
  const loanAppToSubmit = {
    ...loanApp.value,
    applicationType: 'scenario' as ApplicationType,
    ...(marketingReferralTypeId.value && { referralTypeId: marketingReferralTypeId.value }),
    ...(marketingReferralOtherDescription.value && {
      referralOtherDescription: marketingReferralOtherDescription.value,
    }),
    ...(marketingCustomerAware.value && { customerAwareOfBridgit: marketingCustomerAware.value }),
  };

  if (isLoanAppComplete(loanAppToSubmit)) {
    const result = await submitScenario({
      loanApp: loanAppToSubmit,
      sectionStates,
      unaccreditedBroker,
    });

    if (result.success) {
      toast({ type: 'success', title: 'Quote submitted successfully' });
      updateToken(null);
      saveAndGoNext();
    } else {
      toast({
        type: 'danger',
        title: 'Error',
        description: `A system error has occurred. Please try again otherwise contact ${AppValues.COMPANY_NAME} on ${AppValues.CONTACT} for support`,
      });
    }
  } else {
    console.error('loan incomplete!', loanAppToSubmit);
    toast({
      type: 'danger',
      title: 'Error',
      description: `Could not submit your quote. It looks like something is missing in the quote. Please contact ${AppValues.COMPANY_NAME} on ${AppValues.CONTACT} for support.`,
    });
  }
};

const getBackupEmail = () => {
  const { userType, brokerEmail, crmEmail } = loanApp.value;

  switch (userType) {
    case 'Broker':
      return brokerEmail ?? '';
    case 'Crm': {
      return crmEmail ?? '';
    }
    case 'UnaccreditedBroker': {
      return unaccreditedBroker?.email ?? '';
    }
    default: {
      return '';
    }
  }
};

backupQuote(
  {
    ...loanApp.value,
    applicationType: 'scenario' as ApplicationType,
  },
  sectionStates,
  getBackupEmail(),
  unaccreditedBroker,
);
</script>

<template>
  <div class="w-full">
    <div class="mb-8">
      <MarketingQuestions
        v-if="loanApp.userType === 'Broker' && loanApp.brokerId"
        :broker-id="loanApp.brokerId"
        :origin-type="brokerOriginType"
        display-context="broker"
        @update:referral-type-id="(value) => (marketingReferralTypeId = value)"
        @update:referral-other-description="(value) => (marketingReferralOtherDescription = value)"
        @update:customer-aware-of-bridgit="(value) => (marketingCustomerAware = value)"
      />
    </div>
    <div>
      <div>
        <h2 class="mr-1 inline text-lg font-medium">
          Please provide any additional information regarding
          {{ loanApp.userType === 'Primary' ? 'your' : 'the' }} loan scenario
        </h2>
        <span class="text-xs">(optional)</span>
      </div>
      <div>
        <p>
          Sharing additional information can help speed up the quote process for you, and it gives our team a better
          picture of your unique situation. If there's anything you'd like to add, particularly about your asset
          position, feel free to share in the space below.
        </p>
      </div>
      <BxTextarea
        id="submission-reason-for-fund"
        v-model="loanApp.summaryNotes"
        data-test-id="submission-reason-for-fund"
        class="mt-2 w-full"
        :rows="5"
      />
    </div>
    <div class="mt-12">
      <BxButton
        id="submission-submit"
        data-test-id="submission-submit"
        variant="primary"
        :loading="isLoading"
        @click="saveAndContinue()"
        >Submit</BxButton
      >
      <BxButton variant="link" class="ml-4" data-test-id="submission-back" :disabled="isLoading" @click="goBack()"
        >Back</BxButton
      >
    </div>
  </div>
</template>
